import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const signingIn = useRef(false);

  // Firebase Auth keeps the session; whenever it reports a signed-in user we
  // load the tenant profile (role, permissions, company) from the API.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (signingIn.current) return; // login() handles its own profile load
      if (firebaseUser) {
        try {
          const data = await authService.verifyToken();
          setUser(data.user);
        } catch (err) {
          console.error('Auth check failed:', err);
          await signOut(auth).catch(() => {});
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = useCallback(async (credentials) => {
    signingIn.current = true;
    try {
      setError(null);
      const response = await authService.login(credentials);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      signingIn.current = false;
      setLoading(false);
    }
  }, []);

  const loginWithProvider = useCallback(async (providerId) => {
    signingIn.current = true;
    try {
      setError(null);
      const response = await authService.loginWithProvider(providerId);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Sign-in failed');
      throw err;
    } finally {
      signingIn.current = false;
      setLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(() => loginWithProvider('google'), [loginWithProvider]);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setError(null);
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      setError(null);
      const response = await authService.updateProfile(profileData);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Profile update failed');
      throw err;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const data = await authService.verifyToken();
    setUser(data.user);
    return data.user;
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    loginWithGoogle,
    loginWithProvider,
    logout,
    updateProfile,
    refreshUser,
    changePassword: authService.changePassword,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
